import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Plus, Trash2, Pencil, Loader2, Send, Clock, FileText, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { value: '1', label: 'Hoje / Últimas 24h' },
  { value: '7', label: 'Últimos 7 dias' },
  { value: '30', label: 'Últimos 30 dias' },
  { value: '60', label: 'Últimos 60 dias' },
  { value: '90', label: 'Últimos 90 dias' },
];

const HOURS_BRT = Array.from({ length: 24 }, (_, i) => {
  const utc = (i + 3) % 24;
  return { value: String(utc), label: `${String(i).padStart(2, '0')}:00 (BRT)` };
});

export function DailySummaryPage() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [form, setForm] = useState({
    name: '', enabled: true, period: '1', send_hour_utc: '10',
    recipients: [], include_ai_analysis: true, include_top_hosts: true, include_severity: true,
  });
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/daily-summary`, { headers: getAuthHeader() });
      setConfigs(res.data.configs || []);
    } catch (err) { toast.error('Erro ao carregar'); }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addEmail = () => {
    if (!newEmail || !newEmail.includes('@')) return;
    if (form.recipients.includes(newEmail)) return;
    setForm({ ...form, recipients: [...form.recipients, newEmail] });
    setNewEmail('');
  };

  const removeEmail = (email) => {
    setForm({ ...form, recipients: form.recipients.filter(e => e !== email) });
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Nome é obrigatório'); return; }
    if (form.recipients.length === 0) { toast.error('Adicione ao menos um destinatário'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await axios.put(`${API}/settings/daily-summary/${editItem.id}`, form, { headers: getAuthHeader() });
        toast.success('Configuração atualizada');
      } else {
        await axios.post(`${API}/settings/daily-summary`, form, { headers: getAuthHeader() });
        toast.success('Resumo criado');
      }
      setShowForm(false); setEditItem(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta configuração?')) return;
    await axios.delete(`${API}/settings/daily-summary/${id}`, { headers: getAuthHeader() });
    toast.success('Excluído'); fetchData();
  };

  const handleToggle = async (cfg) => {
    await axios.put(`${API}/settings/daily-summary/${cfg.id}`, { enabled: !cfg.enabled }, { headers: getAuthHeader() });
    fetchData();
  };

  const handleSendNow = async (cfg) => {
    setSending(cfg.id);
    try {
      const res = await axios.post(`${API}/settings/daily-summary/${cfg.id}/send-now`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao enviar'); }
    finally { setSending(null); }
  };

  const openEdit = (cfg) => {
    setEditItem(cfg);
    setForm({
      name: cfg.name, enabled: cfg.enabled, period: String(cfg.period || '1'),
      send_hour_utc: String(cfg.send_hour_utc || '10'),
      recipients: cfg.recipients || [],
      include_ai_analysis: cfg.include_ai_analysis !== false,
      include_top_hosts: cfg.include_top_hosts !== false,
      include_severity: cfg.include_severity !== false,
    });
    setShowForm(true);
  };

  const openCreate = () => {
    setEditItem(null);
    setForm({ name: '', enabled: true, period: '1', send_hour_utc: '10', recipients: [], include_ai_analysis: true, include_top_hosts: true, include_severity: true });
    setShowForm(true);
  };

  if (loading) return <Skeleton className="h-[400px] w-full" />;

  const getPeriodLabel = (p) => PERIODS.find(x => x.value === String(p))?.label || p;
  const getHourLabel = (h) => HOURS_BRT.find(x => x.value === String(h))?.label || `${h}:00 UTC`;

  return (
    <div className="space-y-6" data-testid="daily-summary-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Resumo Executivo</h2>
          <p className="text-sm text-muted-foreground">Configure envios automáticos de resumo executivo com análise AI por email</p>
        </div>
        <Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" />Novo Resumo</Button>
      </div>

      {configs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Mail className="h-12 w-12 text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground">Nenhum resumo configurado</p>
            <Button variant="outline" className="mt-4" onClick={openCreate}>Criar primeiro resumo</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {configs.map(cfg => (
            <Card key={cfg.id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <Switch checked={cfg.enabled} onCheckedChange={() => handleToggle(cfg)} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm">{cfg.name}</p>
                      <Badge variant={cfg.enabled ? 'default' : 'secondary'} className="text-xs">{cfg.enabled ? 'Ativo' : 'Inativo'}</Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{getHourLabel(cfg.send_hour_utc)}</span>
                      <span className="flex items-center gap-1"><FileText className="h-3 w-3" />{getPeriodLabel(cfg.period)}</span>
                      <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{(cfg.recipients || []).length} destinatário(s)</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {(cfg.recipients || []).map(e => <Badge key={e} variant="outline" className="text-xs">{e}</Badge>)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-4">
                  <Button variant="outline" size="sm" onClick={() => handleSendNow(cfg)} disabled={sending === cfg.id}>
                    {sending === cfg.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    <span className="ml-1.5">Enviar Agora</span>
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(cfg)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(cfg.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); setEditItem(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editItem ? 'Editar Resumo' : 'Novo Resumo Executivo'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Resumo Diário NOC" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Período dos Dados</Label>
                <Select value={form.period} onValueChange={(v) => setForm({ ...form, period: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PERIODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Horário de Envio</Label>
                <Select value={form.send_hour_utc} onValueChange={(v) => setForm({ ...form, send_hour_utc: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {HOURS_BRT.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Destinatários</Label>
              <div className="flex gap-2">
                <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="email@empresa.com" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }} />
                <Button variant="outline" onClick={addEmail} type="button">Adicionar</Button>
              </div>
              {form.recipients.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {form.recipients.map(email => (
                    <Badge key={email} variant="secondary" className="text-xs flex items-center gap-1 pr-1">
                      {email}
                      <button onClick={() => removeEmail(email)} className="hover:bg-muted rounded p-0.5"><X className="h-3 w-3" /></button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3 pt-2 border-t">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Conteúdo do Email</p>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Análise com IA</Label>
                <Switch checked={form.include_ai_analysis} onCheckedChange={(v) => setForm({ ...form, include_ai_analysis: v })} />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Top Hosts Problemáticos</Label>
                <Switch checked={form.include_top_hosts} onCheckedChange={(v) => setForm({ ...form, include_top_hosts: v })} />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Distribuição por Severidade</Label>
                <Switch checked={form.include_severity} onCheckedChange={(v) => setForm({ ...form, include_severity: v })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditItem(null); }}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editItem ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
